/* eslint-disable eqeqeq */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Form, Row, Button } from "react-bootstrap";
import FormControl from "../../components/Common/FormControl";
import FormLabel from "../../components/Common/FormLabel";
import { getClientInformation } from "../../redux/Slices/clientInformationSlice";
import { Select } from "antd";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import {
  ACTIVITY_LEVEL,
  ALLERGIES,
  DIETARY_PREFERENCES,
} from "../../utils/Constant/ClientConstant";
import ToastNotification from "../../components/Common/ToastNotification";
import {
  createMealPlanThunk,
  fetchMacroSplitTemplatesThunk,
  fetchMealPlanDetails,
  fetchRecipesBasedMealType,
  resetFilteredRecipes,
  resetMealPlanDetails,
  resetState,
} from "../../redux/Slices/clientMealsTabsSlice";
import Loader from "../../components/Common/Loader";
import {
  calculateMacronutrients,
  isValidDecimalInput,
  isValidNumberInput,
} from "../../utils/Utils";
import FormSelect from "../../components/Common/FormSelect";
import RecipesCard from "./RecipesCard";
import {
  CONSTANTS,
  DIETARY_CONFLICTS_BY_NAME,
  MEAL_CATEGORIES,
  MEAL_PERCENTAGE_DEFAULTS,
  MEAL_PLAN_TYPES,
  MEAL_TYPES,
  mealPlanMap,
} from "../../utils/Constant/Constant";
import MealPieChart from "./MealPieChart";
import MealPlanSummary from "./MealPlanSummary";
import appInsights from "../../AppInsights";

const { Option } = Select;

const activityMultipliers = {
  VeryLight: 1.2,
  Light: 1.375,
  Moderate: 1.55,
  Active: 1.8,
};

const ClientMealsDetails = ({ coachId, clientId, status }) => {
  const dispatch = useDispatch();

  const { clientData } = useSelector((state) => state.clientInformation);
  const { trans } = useCustomTranslation();

  const {
    filteredRecipes,
    loading,
    filterIsLoading,
    createMealPlanLoading,
    createMealPlanSuccessMessage,
    createMealPlanErrorMessage,
    mealPlanDetails,
    mealPlanDetailsLoading,
    macroSplitTemplates,
    macroSplitTemplatesLoading,
  } = useSelector((state) => state.mealTags);
  const {
    firstName,
    clientMeasurements,
    currentWeightUpdated,
    dailyIntakeRecommendation,
  } = useSelector((state) => state.membershipOverview);
  const [dietaryPreferences, setDietaryPreferences] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [mealRestrictions, setMealRestrictions] = useState([]);
  const [selectedMeals, setSelectedMeals] = useState(
    MEAL_PLAN_TYPES.THREE_MEALS
  );
  const [selectedActivityLevel, setSelectedActivityLevel] = useState(null);
  const [displayedRecipes, setDisplayedRecipes] = useState([]);
  const [toastConfig, setToastConfig] = useState(null);
  // checking mealsDetails api response
  const [isMealsDetailsAlreadyNotFilled, setIsMealsDetailsAlreadyNotFilled] =
    useState(false);
  const [mealPercentages, setMealPercentages] = useState({});
  // maintain flag for changing recipes if any parameter changes
  const [isFetchingRecipes, setIsFetchingRecipes] = useState(false);
  //check total percentage of protein, carb and fat should 100%
  const [isPercentageValid, setIsPercentageValid] = useState(true);
  // Check each meal percentage should be 100%
  const [isEachMealPercentageValid, setIsEachMealPercentageValid] =
    useState(true);
  const [makeFavoriteMessage, setMakeFavoriteMessage] = useState("");
  // Macro Split Templates
  const [selectedMacroTemplate, setSelectedMacroTemplate] = useState(null);
  //number of recipes per meal
  const [numberOfRecipesPerMeal, setNumberOfRecipesPerMeal] = useState(4);
  const [lastEditedField, setLastEditedField] = useState(null);
  const [resizeConfirmed, setResizeConfirmed] = useState(false);

  const [formData, setFormData] = useState({
    bmrTdee: "",
    pal: "",
    mealPlanName: "",
    dailyIntake: "",
    proteinPercentage: "",
    proteinGrams: "",
    carbsPercentage: "",
    carbsGrams: "",
    fatPercentage: "",
    fatGrams: "",
    recipesDetailMeal: [],
  });

  const [errors, setErrors] = useState({});
  const [recipeIds, setRecipeIds] = useState([]);

  // Initialize with default 3 meals on first load
  useEffect(() => {
    if (coachId) {
      dispatch(resetMealPlanDetails());
      dispatch(getClientInformation({ clientId, coachId }));
      dispatch(fetchMealPlanDetails({ clientId }));
      dispatch(fetchMacroSplitTemplatesThunk());
      
      // Track page view
      appInsights.trackPageView({
        name: "ClientMealPlan_PageView",
        properties: { clientId, coachId },
      });

    }
  }, [dispatch, clientId, coachId]);

  useEffect(() => {
    if (mealPlanDetails) {
      const mealPlan = mealPlanDetails || {};

      setFormData((prev) => ({
        ...prev,
        mealPlanName: mealPlan.mealPlanName || "",
        dailyIntake: mealPlan.dailyIntake || "",
        proteinPercentage: mealPlan.proteinPercentage || "",
        proteinGrams: mealPlan.proteinGrams,
        carbsPercentage: mealPlan.carbsPercentage || "",
        carbsGrams: mealPlan.carbsGrams,
        fatPercentage: mealPlan.fatPercentage || "",
        fatGrams: mealPlan.fatGrams,
        mealsPerDay: selectedMeals,
        recipesDetailMeal: mealPlan.recipesDetailMeal || [],
      }));
      setSelectedMeals(mealPlan?.mealsPerDay[0] || MEAL_PLAN_TYPES.THREE_MEALS);
      setDisplayedRecipes([]);
      mealPlan?.recipesDetailMeal?.length > 0
        ? setIsMealsDetailsAlreadyNotFilled(false)
        : setIsMealsDetailsAlreadyNotFilled(true);
      setMealPercentages(mealPlan?.mealsPerDayPercentage || {});
      setSelectedMacroTemplate(mealPlan?.macroSplitTemplateId || 1);
      setNumberOfRecipesPerMeal(mealPlan?.numberOfRecipesPerMeal || 4);

      // Use saved percentages or defaults
      if (
        mealPlan?.mealsPerDayPercentage &&
        Object.keys(mealPlan.mealsPerDayPercentage).length > 0
      ) {
        setMealPercentages(mealPlan.mealsPerDayPercentage);
      } else {
        const mealType =
          mealPlan?.mealsPerDay[0] || MEAL_PLAN_TYPES.THREE_MEALS;
        setMealPercentages(MEAL_PERCENTAGE_DEFAULTS[mealType] || {});
      }
    } else {
      setFormData({
        bmrTdee: "",
        pal: "",
        mealPlanName: "",
        dailyIntake: "",
        proteinPercentage: "",
        proteinGrams: 0,
        carbsPercentage: "",
        carbsGrams: 0,
        fatPercentage: "",
        fatGrams: 0,
        recipesDetailMeal: [],
      });
      setIsMealsDetailsAlreadyNotFilled(true);
      setSelectedMeals(MEAL_PLAN_TYPES.THREE_MEALS);
      setAllergies([]);
      setSelectedMacroTemplate(1);
      // Set default percentages for 3 meals
      setMealPercentages(MEAL_PERCENTAGE_DEFAULTS[MEAL_PLAN_TYPES.THREE_MEALS]);
      setNumberOfRecipesPerMeal(4);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mealPlanDetails]);

  // Check percentage totals for protein, carbs and fats
  useEffect(() => {
    const protein = Number(formData.proteinPercentage) || 0;
    const carbs = Number(formData.carbsPercentage) || 0;
    const fat = Number(formData.fatPercentage) || 0;

    const total = protein + carbs + fat;
    setIsPercentageValid(total === 100);
  }, [
    formData.proteinPercentage,
    formData.carbsPercentage,
    formData.fatPercentage,
  ]);

  // Check if each meal percentage is valid
  useEffect(() => {
    const total = Object.values(mealPercentages).reduce(
      (a, b) => a + (isNaN(Number(b)) ? 0 : Number(b)),
      0
    );

    const roundedTotal = Math.round(total * 10) / 10;
    const eachMealValid = roundedTotal === 100;
    setIsEachMealPercentageValid(eachMealValid);

    // Auto-adjust rounding snap (E1) - only when close to 100
    if (
      !eachMealValid &&
      lastEditedField &&
      roundedTotal >= 99.9 &&
      roundedTotal <= 100.1
    ) {
      const adjustment = 100 - roundedTotal;
      setMealPercentages((prev) => ({
        ...prev,
        [lastEditedField]: Math.round(
          Number(prev[lastEditedField]) + adjustment,
        ),
      }));
    }
  }, [mealPercentages, lastEditedField]);

  useEffect(() => {
    if (selectedMacroTemplate === 1 && macroSplitTemplates.length > 0) {
      const template = macroSplitTemplates.find(
        (macroTemplate) => macroTemplate.id === selectedMacroTemplate
      );
      setFormData((prev) => ({
        ...prev,
        proteinPercentage: template?.protein,
        carbsPercentage: template?.carbs,
        fatPercentage: template?.fats,
      }));
    }
  }, [macroSplitTemplates, selectedMacroTemplate]);

  // Add this at the top of your component (outside the function)
  let fetchTimeout = null;

  const fetchFilteredRecipes = async (
    selectedMealsParam,
    preferenceId,
    selectedAllergiesId,
    newMealPercentages,
    currentFormData = formData,
    selectedRecipes
  ) => {
    // Clear any existing timeout
    if (fetchTimeout) {
      clearTimeout(fetchTimeout);
    }

    // Set a new timeout for debouncing (500ms delay)
    fetchTimeout = setTimeout(async () => {
      setIsFetchingRecipes(true);
      try {
        const newMealTimes = mealPlanMap[selectedMealsParam] || [];

        // Clear existing recipes first
        setDisplayedRecipes([]);
        dispatch(resetFilteredRecipes());

        // Use the passed currentFormData or fall back to state
        const formDataToUse = currentFormData || formData;

        await Promise.all(
          newMealTimes.map((mealTime) => {
            // Get the percentage for THIS specific meal type
            const percentage = newMealPercentages[mealTime] || 0;
            const kcal = formDataToUse?.dailyIntake
              ? Math.round((formDataToUse?.dailyIntake * percentage) / 100)
              : 0;
            // Determine recipe count based on mealTime
            const isSnackOrLunch1 =
              mealTime === "SNACK1" || mealTime === "LUNCH1";
            const receipeCount = isSnackOrLunch1
              ? numberOfRecipesPerMeal
                ? numberOfRecipesPerMeal * 2
                : 8
              : numberOfRecipesPerMeal || 4;
              
              let selectedRecipeId = (
                  isMealsDetailsAlreadyNotFilled
                    ? selectedRecipes
                    : formData.recipesDetailMeal || []
                )
                  .filter((r) => r.mealName?.toUpperCase() === mealTime)
                  .map((r) => r.recipeId)
                  .filter(Boolean);

            const requestPayload = {
              carbs: formDataToUse.carbsPercentage.toString(),
              fats: formDataToUse.fatPercentage.toString(),
              protein: formDataToUse.proteinPercentage.toString(),
              receipeCount: receipeCount,
              targetCalories: parseInt(kcal),
              preferences: Array.isArray(preferenceId)
                ? preferenceId.join(",")
                : preferenceId,
              allergies: Array.isArray(selectedAllergiesId)
                ? selectedAllergiesId?.join(",")
                : selectedAllergiesId,
              isCustomized: true
            };
            if(resizeConfirmed){
              requestPayload.resizeDailyIntake = true;
              requestPayload.selectedRecipeId = selectedRecipeId;
            }

            // console.log('test_requestPayload',mealTime +" :"+ requestPayload)
            return dispatch(
              fetchRecipesBasedMealType({ requestPayload, mealTime })
            );
          })
        );
      } finally {
        setIsFetchingRecipes(false);
        setResizeConfirmed(false);
      }
    }, 500); // 500ms debounce delay
  };

  // Add cleanup in useEffect
  useEffect(() => {
    return () => {
      // Clean up the timeout when component unmounts
      if (fetchTimeout) {
        clearTimeout(fetchTimeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      mealPlanDetails?.recipesDetailMeal?.length > 0 &&
      mealPlanDetails?.mealsPerDay?.[0] === selectedMeals &&
      !isMealsDetailsAlreadyNotFilled
    ) {
      const updatedRecipes = [];

      const grouped = mealPlanDetails?.recipesDetailMeal?.reduce(
        (acc, item) => {
          const mealType = item.mealName.toUpperCase();
          if (!acc[mealType]) {
            acc[mealType] = [];
          }
          acc[mealType].push({
            recipeId: item.recipeId,
            recipe: item.recipe,
          });
          return acc;
        },
        {}
      );

      const addRecipes = (mealType, recipes) => {
        if (recipes?.length > 0) {
          recipes.forEach((recipe) => {
            return updatedRecipes.push({
              mealName: mealType,
              recipeId: recipe.id || recipe.recipeId,
              recipe: recipe.recipe,
            });
          });
        }
      };

      // Push recipes for each meal category
      addRecipes(MEAL_TYPES.BREAKFAST, grouped?.BREAKFAST);
      addRecipes(MEAL_TYPES.LUNCH, grouped?.LUNCH);
      addRecipes(MEAL_TYPES.LUNCH1, grouped?.LUNCH1);
      addRecipes(MEAL_TYPES.DINNER, grouped?.DINNER);
      addRecipes(MEAL_TYPES.SNACK, grouped?.SNACK);
      addRecipes(MEAL_TYPES.SNACK1, grouped?.SNACK1);

      // console.log("test_updatedRecipes_1", updatedRecipes);

      setDisplayedRecipes(updatedRecipes);
    } else {
      const updatedRecipes = [];

      const addRecipes = (mealType, recipes, startIndex = 0, endIndex = 4) => {
        if (recipes?.length > 0) {
          recipes.slice(startIndex, endIndex).forEach((recipe) => {
            updatedRecipes.push({
              mealName: mealType,
              recipeId: recipe.recipeId,
              recipe: recipe,
            });
          });
        }
      };

      // Push recipes for each meal category
      addRecipes(
        MEAL_TYPES.BREAKFAST,
        filteredRecipes?.BREAKFAST,
        0,
        numberOfRecipesPerMeal
      );
      addRecipes(
        MEAL_TYPES.LUNCH,
        filteredRecipes?.LUNCH,
        0,
        numberOfRecipesPerMeal
      );
      // Get last 4 recipes from LUNCH1 array
      const lunch1Length = filteredRecipes?.LUNCH1?.length || 0;
      const lunch1Start = Math.max(0, lunch1Length - numberOfRecipesPerMeal);
      addRecipes(
        MEAL_TYPES.LUNCH1,
        filteredRecipes?.LUNCH1,
        lunch1Start,
        lunch1Length
      );

      addRecipes(
        MEAL_TYPES.DINNER,
        filteredRecipes?.DINNER,
        0,
        numberOfRecipesPerMeal
      );
      addRecipes(
        MEAL_TYPES.SNACK,
        filteredRecipes?.SNACK,
        0,
        numberOfRecipesPerMeal
      );
      // Get last 4 recipes from SNACK1 array
      const snack1Length = filteredRecipes?.SNACK1?.length || 0;
      const snack1Start = Math.max(0, snack1Length - numberOfRecipesPerMeal);
      addRecipes(
        MEAL_TYPES.SNACK1,
        filteredRecipes?.SNACK1,
        snack1Start,
        snack1Length
      );
      setDisplayedRecipes(updatedRecipes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRecipes]);

  useEffect(() => {
    if (clientData?.clientProvidedInputs) {
      const dietary = clientData.clientProvidedInputs.find(
        (input) => input.title === DIETARY_PREFERENCES
      );

      const allergy = clientData.clientProvidedInputs.find(
        (input) => input.title === ALLERGIES
      );

      const activityLevel = clientData.clientProvidedInputs.find(
        (input) => input.title === ACTIVITY_LEVEL
      );

      if (
        mealPlanDetails?.recipesDetailMeal?.length === 0 ||
        mealPlanDetails === null
      ) {
        setDietaryPreferences(
          dietary?.selectedOptionIds
            ? dietary.selectedOptionIds
                .split(",")
                .map(
                  (id) =>
                    dietary.options.find((opt) => opt.optionId === Number(id))
                      ?.optionId
                )
                .filter(Boolean)
            : []
        );

        setAllergies(
          allergy?.selectedOptionIds
            ? allergy.selectedOptionIds
                .split(",")
                .map(
                  (id) =>
                    allergy.options.find((opt) => opt.optionId === Number(id))
                      ?.optionId
                )
                .filter(Boolean)
            : []
        );
        setMealRestrictions(
          allergy?.selectedOptionIds
            ? allergy.selectedOptionIds
                .split(",")
                .map(
                  (id) =>
                    allergy.options.find((opt) => opt.optionId === Number(id))
                      ?.optionText
                )
                .filter(Boolean)
            : []
        );
      } else {
        setDietaryPreferences(
          mealPlanDetails?.preferences.join(",")
            ? mealPlanDetails?.preferences
                .join(",")
                .split(",")
                .map(
                  (id) =>
                    dietary.options.find((opt) => opt.optionId === Number(id))
                      ?.optionId
                )
                .filter(Boolean)
            : []
        );

        setAllergies(
          mealPlanDetails?.allergies.join(",")
            ? mealPlanDetails?.allergies
                .join(",")
                .split(",")
                .map(
                  (id) =>
                    allergy.options.find((opt) => opt.optionId === Number(id))
                      ?.optionId
                )
                .filter(Boolean)
            : []
        );
        setMealRestrictions(
          mealPlanDetails?.allergies.join(",")
            ? mealPlanDetails?.allergies
                .join(",")
                .split(",")
                .map(
                  (id) =>
                    allergy.options.find((opt) => opt.optionId === Number(id))
                      ?.optionText
                )
                .filter(Boolean)
            : []
        );
      }

      const selectedActivityLevelOption =
        activityLevel?.options.find(
          (opt) => opt.optionId === Number(activityLevel.selectedOptionIds)
        )?.optionText || "";

      setSelectedActivityLevel(selectedActivityLevelOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientData, mealPlanDetails]);

  // BMR Calculation
  useEffect(() => {
    if (
      clientData?.height &&
      clientData?.weight &&
      clientMeasurements?.age &&
      currentWeightUpdated
    ) {
      const { age } = clientMeasurements;
      const { weight, height, gender, sexAtBirth } = clientData;
      let BMR;


    const g = gender?.toLowerCase();
    const s = sexAtBirth?.toLowerCase();
      
    if (g === "male" || g === "other" || s === "male" || s === "other") {
        BMR = (10 * currentWeightUpdated ||weight) + (6.25 * height) - (5 * age) + 5;
      } else {
        BMR = (10 * currentWeightUpdated || weight) + (6.25 * height) - (5 * age) - 161;
      }

      const activityMultiplier =
      activityMultipliers[selectedActivityLevel?.trim()] || 1.2;
      const TDEE = BMR * activityMultiplier;
      const PAL = TDEE / BMR;

      setFormData((prev) => ({
        ...prev,
        bmrTdee: `${Math.round(BMR)} / ${Math.round(TDEE)}`,
        pal: `${PAL}`,
        mealPlanName: firstName
          ? `${firstName}'s Meal Plan`
          : prev.mealPlanName || "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        bmrTdee: `N/A`,
        pal: `N/A`,
        mealPlanName: firstName
          ? `${firstName}'s Meal Plan`
          : prev.mealPlanName || "",
      }));
    }
  }, [
    clientMeasurements,
    clientData,
    selectedActivityLevel,
    firstName,
    currentWeightUpdated,
  ]);
  // Add a useEffect to calculate macronutrients when percentages or daily intake change
  useEffect(() => {
    if (formData.dailyIntake) {
      const { protein, carbs, fat } = calculateMacronutrients(
        Number(formData.dailyIntake),
        Number(formData.proteinPercentage),
        Number(formData.carbsPercentage),
        Number(formData.fatPercentage)
      );

      setFormData((prev) => ({
        ...prev,
        proteinGrams: Math.round(protein),
        carbsGrams: Math.round(carbs),
        fatGrams: Math.round(fat),
      }));
    }
  }, [
    formData.dailyIntake,
    formData.proteinPercentage,
    formData.carbsPercentage,
    formData.fatPercentage,
  ]);
  const handleChange = (e) => {
    const { name, value } = e.target;
    // Validation for different input types
    if (name === CONSTANTS.DAILY_INTAKE) {
      if (!isValidNumberInput(value) || value.length > CONSTANTS.FOUR) return;
    } else if (
      name === "proteinPercentage" ||
      name === "carbsPercentage" ||
      name === "fatPercentage"
    ) {
      if (value !== "" && !isValidNumberInput(value)) return;
    } else if (
      name === "proteinGrams" ||
      name === "carbsGrams" ||
      name === "fatGrams"
    ) {
      if (value !== "" && !isValidDecimalInput(value)) return;
    }

    // Update state first
    const updatedFormData = {
      ...formData,
      [name]: value,
    };
    setFormData(updatedFormData);

    // Clear error message
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    let newErrors = {};
    if (!formData.mealPlanName)
      newErrors.mealPlanName =
        trans("ClientMealPlan.mealPlanName") + " " + trans("Common.isRequired");
    if (!formData.dailyIntake)
      newErrors.dailyIntake =
        trans("ClientMealPlan.dailyIntake") + " " + trans("Common.isRequired");

    if (!selectedMeals || selectedMeals === "") {
      newErrors.selectedMeals = "Select meal is required";
    }

    // Convert percentages to numbers and check if their total exceeds 100%
    const protein = Number(formData.proteinPercentage) || CONSTANTS.ZERO;
    const carbs = Number(formData.carbsPercentage) || CONSTANTS.ZERO;
    const fat = Number(formData.fatPercentage) || CONSTANTS.ZERO;

    // Check if any value is zero or negative
    if (protein < CONSTANTS.FIVE) {
      newErrors.proteinPercentage = trans(
        "ClientMealPlan.proteinMustBeGreaterThanFive"
      );
      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        message: trans("ClientMealPlan.proteinMustBeGreaterThanFive"),
        heading: trans("Common.failed"),
      });

      setTimeout(() => {
        setToastConfig(null);
      }, CONSTANTS.TOAST_DURATION_SHORT);
    }
    if (carbs < CONSTANTS.FIVE) {
      newErrors.carbsPercentage = trans(
        "ClientMealPlan.carbsMustBeGreaterThanFive"
      );
      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        message: trans("ClientMealPlan.carbsMustBeGreaterThanFive"),
        heading: trans("Common.failed"),
      });

      setTimeout(() => {
        setToastConfig(null);
      }, CONSTANTS.TOAST_DURATION_SHORT);
    }
    if (fat < CONSTANTS.FIVE) {
      newErrors.fatPercentage = trans(
        "ClientMealPlan.fatMustBeGreaterThanFive"
      );
      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        message: trans("ClientMealPlan.fatMustBeGreaterThanFive"),
        heading: trans("Common.failed"),
      });

      setTimeout(() => {
        setToastConfig(null);
      }, CONSTANTS.TOAST_DURATION_SHORT);
    }

    // Check if total percentage exceeds 100
    else if (!isPercentageValid) {
      newErrors.proteinPercentage = trans(
        "ClientMealPlan.proteinPercentageError"
      );
      newErrors.carbsPercentage = trans(
        "ClientMealPlan.proteinPercentageError"
      );
      newErrors.fatPercentage = trans("ClientMealPlan.proteinPercentageError");

      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        message: trans("ClientMealPlan.proteinPercentageError"),
        heading: trans("Common.failed"),
      });

      setTimeout(() => {
        setToastConfig(null);
      }, CONSTANTS.TOAST_DURATION_SHORT);
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === CONSTANTS.ZERO;
  };

  const isMacroPercentageValid = () => {
    const protein = Number(formData?.proteinPercentage) || 0;
    const carbs = Number(formData?.carbsPercentage) || 0;
    const fat = Number(formData?.fatPercentage) || 0;

    return (
      protein >= CONSTANTS.FIVE &&
      carbs >= CONSTANTS.FIVE &&
      fat >= CONSTANTS.FIVE
    );
  };

  const handleFetchRecipes = () => {
    if (validateForm()) {
      // AppInsights – track generate click
    appInsights.trackEvent({
        name: "ClientMealPlan_GenerateMealPlan",
        properties: {
          clientId,
          coachId,
          mealsPerDay: selectedMeals,
          allergies,
          dietaryPreferences,
          mealPercentages,
          formData,
        },
      });

      if (isPercentageValid && isEachMealPercentageValid) {
        let selectedRecipes=displayedRecipes
        fetchFilteredRecipes(
          selectedMeals,
          dietaryPreferences,
          allergies,
          mealPercentages,
          formData,
          selectedRecipes
        );
        setIsMealsDetailsAlreadyNotFilled(true);
        setDisplayedRecipes([]);
      }
    }
  };
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    let selectedMealDetails;
    if (isMealsDetailsAlreadyNotFilled) {
      selectedMealDetails = displayedRecipes;
    } else {
      selectedMealDetails = formData.recipesDetailMeal?.map((item) => ({
        mealName: item.mealName,
        recipeId: item.recipeId,
        recipe: item.recipe,
      }));
    }
    if (validateForm()) {
      const requestPayload = {
        ...formData,
        bmrTdee: formData.bmrTdee === "N/A" ? "0" : formData.bmrTdee || "0",
        pal: formData.pal === "N/A" ? 0 : formData.pal || 0,
        preferences: dietaryPreferences?.join(","),
        allergies: allergies?.join(","),
        mealsPerDay: selectedMeals,
        clientId,
        coachId,
        recipesDetailMeal: selectedMealDetails,
        mealsPerDayPercentage: mealPercentages,
        macroSplitTemplateId: selectedMacroTemplate,
        numberOfRecipesPerMeal,
      };

      // console.log("test_create_meal", requestPayload);
      isPercentageValid &&
        isEachMealPercentageValid &&
        dispatch(createMealPlanThunk(requestPayload));

        // AppInsights - track save click
        appInsights.trackEvent({
          name: "ClientMealPlan_SaveMealPlan",
          properties: {
            clientId,
            coachId,
            mealsPerDay: selectedMeals,
            macroSplitTemplateId: selectedMacroTemplate,
            numberOfRecipesPerMeal,
          },
        });

    }
  };

  const handleReset = () => {
    if (coachId) {
      dispatch(resetMealPlanDetails());
      dispatch(getClientInformation({ clientId, coachId }));
      dispatch(fetchMealPlanDetails({ clientId }));
      setResizeConfirmed(false); 

      // AppInsights - track reset click
      appInsights.trackEvent({
        name: "ClientMealPlan_ResetClicked",
        properties: { clientId, coachId },
      });

    }
  };

  // // Handle recipe update
  const handleRecipeUpdate = (updatedRecipesList, mealType) => {
    let finalMealDetails = [];
    if (isMealsDetailsAlreadyNotFilled) {
      const filteredMealDetails = displayedRecipes?.filter(
        (item) => item.mealName !== mealType
      );
      const newMealEntries = updatedRecipesList.map((item) => ({
        // mealName: item.mealName || mealType,
        mealName: mealType,
        recipe: item.recipe,
        recipeId: item.recipeId,
      }));

      finalMealDetails = [...filteredMealDetails, ...newMealEntries];
    } else {
      const recipesDetailMeal = formData?.recipesDetailMeal?.map((item) => ({
        mealName: item.mealName,
        recipeId: item.recipeId,
        recipe: item.recipe,
      }));

      const filteredMealDetails = recipesDetailMeal?.filter(
        (item) => item.mealName !== mealType
      );
      const newMealEntries = updatedRecipesList.map((item) => ({
        mealName: mealType,
        // mealName: item.mealName,
        recipeId: item.recipeId,
        recipe: item.recipe,
      }));
      finalMealDetails = [...filteredMealDetails, ...newMealEntries];
    }
    if (validateForm()) {
      const requestPayload = {
        ...formData,
        bmrTdee: formData.bmrTdee === "N/A" ? "0" : formData.bmrTdee || "0",
        pal: formData.pal === "N/A" ? 0 : formData.pal || 0,
        preferences: dietaryPreferences?.join(","),
        allergies: allergies?.join(","),
        mealsPerDay: selectedMeals,
        clientId,
        coachId,
        recipesDetailMeal: finalMealDetails,
        mealsPerDayPercentage: mealPercentages,
        macroSplitTemplateId: selectedMacroTemplate,
        numberOfRecipesPerMeal,
      };

      // AppInsights - track recipe update
      appInsights.trackEvent({
        name: "ClientMealPlan_RecipeUpdated",
        properties: {
          clientId,
          coachId,
          mealType,
        },
      });

      // console.log("test_upsert_meal", requestPayload);
      isPercentageValid &&
        isEachMealPercentageValid &&
        dispatch(createMealPlanThunk(requestPayload));
    }
  };

  // Toast notification logic
  useEffect(() => {
    if (!toastConfig) {
      if (createMealPlanSuccessMessage) {
        // Trigger success toast
        setToastConfig({
          type: CONSTANTS.TOAST_SUCCESS,
          message: createMealPlanSuccessMessage,
          heading: trans("Common.success"),
        });
        setTimeout(() => {
          dispatch(resetState());
          setToastConfig(null);
        }, 3000);
      } else if (createMealPlanErrorMessage) {
        // AppInsights - track save recipe error
        appInsights.trackException({
            exception: new Error(createMealPlanErrorMessage),
            properties: {
              location: "ClientMealPlan_error",
              clientId,
              coachId,
            },
          });
        // Trigger error toast
        setToastConfig({
          type: CONSTANTS.TOAST_ERROR,
          message: createMealPlanErrorMessage,
          heading: trans("Common.failed"),
        });
        setTimeout(() => {
          dispatch(resetState());
          setToastConfig(null);
        }, 3000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createMealPlanSuccessMessage,
    createMealPlanErrorMessage,
    trans,
    dispatch,
    toastConfig,
  ]);

  const groupedRecipes = formData?.recipesDetailMeal?.length
    ? formData.recipesDetailMeal.reduce((acc, recipe) => {
        const key = recipe.mealName.toUpperCase();
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push({
          // recipeId: recipe.recipeId,
          mealName: recipe.mealName,
          recipe: recipe.recipe,
          ...recipe.recipeDetails,
        });
        // console.log("test_recipe_acc", acc);
        return acc;
      }, {})
    : [];

  const handleMealPercentageChange = (meal, value) => {
    setIsMealsDetailsAlreadyNotFilled(true);

    // Validate input
    if (value !== "" && !isValidDecimalInput(value)) return;

    // Ensure value is between 0 and 100
    const numValue = value === "" ? 0 : Number(value);
    if (numValue < 0 || numValue > 100) return;

    const newValue = value === "" ? 0 : Number(value);
    const newPercentages = { ...mealPercentages, [meal]: newValue };

    setMealPercentages(newPercentages);
    setLastEditedField(meal);
    setFormData((prev) => ({ ...prev, recipesDetailMeal: [] }));
  };

  const handleMealPercentageBlur = (meal) => {
    // Calculate total on blur
    const total = Object.values(mealPercentages).reduce(
      (a, b) => a + (isNaN(Number(b)) ? 0 : Number(b)),
      0
    );

    const roundedTotal = Math.round(total * 10) / 10;

    // Apply rounding snap if close to 100 (E1)
    if (roundedTotal >= 99.9 && roundedTotal <= 100.1 && roundedTotal !== 100) {
      const adjustment = 100 - roundedTotal;
      setMealPercentages((prev) => ({
        ...prev,
        [meal]: Math.round(Number(prev[meal]) + adjustment),
      }));
    }
  };

  const handleMealsChange = (e) => {
    const newSelectedMeals = e.target.value;
    setSelectedMeals(newSelectedMeals);
    setFormData((prev) => ({ ...prev, recipesDetailMeal: [] }));

    // Auto-populate with defaults for the selected meal plan (AC2 & AC3)
    const defaultPercentages = MEAL_PERCENTAGE_DEFAULTS[newSelectedMeals] || {};
    setMealPercentages(defaultPercentages);
    setLastEditedField(null);
    setIsMealsDetailsAlreadyNotFilled(true);
  };

  const handleNumberOfRecipesPerMealChange = (event) => {
    const { value } = event.target;
    setNumberOfRecipesPerMeal(Number(value));
    setFormData((prev) => ({ ...prev, recipesDetailMeal: [] }));
  };

  const handleMacroTemplateChange = (value) => {
    const template = macroSplitTemplates.find((t) => t.id === value);
    // console.log("template", template, "value", value);
    setSelectedMacroTemplate(value);

    if (template && template.id !== 11) {
      // Not "Customize"
      setFormData((prev) => ({
        ...prev,
        proteinPercentage: template.protein,
        carbsPercentage: template.carbs,
        fatPercentage: template.fats,
      }));
    } else {
      // Reset to empty for custom values
      setFormData((prev) => ({
        ...prev,
        proteinPercentage: "",
        carbsPercentage: "",
        fatPercentage: "",
      }));
    }
  };
  useEffect(() => {
    const dinner = filteredRecipes[MEAL_CATEGORIES.DINNER]
      ?.slice(0, numberOfRecipesPerMeal)
      ?.map((recipe) => `${recipe.id}^Dinner`);
    const breakfast = filteredRecipes[MEAL_CATEGORIES.BREAKFAST]
      ?.slice(0, numberOfRecipesPerMeal)
      ?.map((recipe) => `${recipe.id}^Breakfast`);
    const lunch = (
      filteredRecipes[MEAL_CATEGORIES.LUNCH] ||
      filteredRecipes[MEAL_CATEGORIES.LUNCH1]
    )
      ?.slice(0, numberOfRecipesPerMeal)
      ?.map((recipe) => `${recipe.id}^Lunch`);
    const snack = (
      filteredRecipes[MEAL_CATEGORIES.SNACK] ||
      filteredRecipes[MEAL_CATEGORIES.SNACK1]
    )
      ?.slice(0, numberOfRecipesPerMeal)
      ?.map((recipe) => `${recipe.id}^Snack`);
    const finalList = [];
    dinner?.forEach((recipe) => finalList.push(recipe));
    breakfast?.forEach((recipe) => finalList.push(recipe));
    lunch?.forEach((recipe) => finalList.push(recipe));
    snack?.forEach((recipe) => finalList.push(recipe));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRecipes, selectedMeals]);

  useEffect(() => {
    const grouped = mealPlanDetails?.recipesDetailMeal?.reduce((acc, item) => {
      const mealType = item.mealName.toUpperCase();
      if (!acc[mealType]) {
        acc[mealType] = [];
      }
      acc[mealType].push({
        recipeId: item.recipeId,
        recipeDetails: item.recipeDetails,
      });
      return acc;
    }, {});

    const dinner = grouped?.[MEAL_CATEGORIES.DINNER]?.map(
      (item) => `${item.recipeId}^Dinner`
    );

    const breakfast = grouped?.[MEAL_CATEGORIES.BREAKFAST]?.map(
      (item) => `${item.recipeId}^Breakfast`
    );

    const lunch = (
      grouped?.[MEAL_CATEGORIES.LUNCH] || grouped?.[MEAL_CATEGORIES.LUNCH1]
    )?.map((item) => `${item.recipeId}^Lunch`);

    const lunch1 = grouped?.[MEAL_CATEGORIES.LUNCH1]?.map(
      (item) => `${item.recipeId}^Lunch1`
    );

    const snack = (
      grouped?.[MEAL_CATEGORIES.SNACK] || grouped?.[MEAL_CATEGORIES.SNACK1]
    )?.map((item) => `${item.recipeId}^Snack`);

    const snack1 = grouped?.[MEAL_CATEGORIES.SNACK1]?.map(
      (item) => `${item.recipeId}^Snack1`
    );

    const finalList = [
      ...(dinner || []),
      ...(breakfast || []),
      ...(lunch || []),
      ...(lunch1 || []),
      ...(snack || []),
      ...(snack1 || []),
    ];

    setRecipeIds(finalList);
  }, [mealPlanDetails, selectedMeals]);

  const handleDietaryPreference = (preferenceId) => {
    setDietaryPreferences(preferenceId);
  };
  const handleChangeAllergies = (selectedIds) => {
    setAllergies(selectedIds);

    const allergyInput = clientData?.clientProvidedInputs?.find(
      (input) => input.title === ALLERGIES
    );

    if (allergyInput?.options) {
      const selectedOptionTexts = selectedIds
        .map((id) => {
          const matched = allergyInput.options.find(
            (opt) => opt.optionId === id
          );
          return matched?.optionText;
        })
        .filter(Boolean);

      setMealRestrictions(selectedOptionTexts);
    }
  };

  useEffect(() => {
    if (makeFavoriteMessage) {
      setToastConfig({
        type: CONSTANTS.TOAST_SUCCESS,
        message: makeFavoriteMessage,
        heading: trans("Common.success"),
      });
      setTimeout(() => {
        setToastConfig(null);
        setMakeFavoriteMessage("");
      }, 3000);
      dispatch(fetchMealPlanDetails({ clientId }));
    }
  }, [makeFavoriteMessage, trans, dispatch, clientId]);

  // Calculate current total percentage
  const currentTotalPercentage = Object.values(mealPercentages).reduce(
    (a, b) => a + (isNaN(Number(b)) ? 0 : Number(b)),
    0
  );
  const roundedCurrentTotal = Math.round(currentTotalPercentage * 10) / 10;

  // Check if all percentages are zero (E2)
  const allZero = Object.values(mealPercentages).some(
    (val) => Number(val) === 0
  );

  // check micro percentage
  const validMacroPercentage = isMacroPercentageValid();

  return (
    <div className="clientDetailsInfoTabContainer vh-100 overflow-auto overflow-x-hidden">
      {loading || createMealPlanLoading || mealPlanDetailsLoading ? (
        <Loader />
      ) : (
        <>
          <Row className="align-items-center">
            <Col>
              <h6>{trans("ClientMealPlan.nutritionInformation")}</h6>
            </Col>
            <Col className="text-end align-items-center">
              <Button
                className="saveButton mr-2"
                disabled={filterIsLoading || mealPlanDetailsLoading || status}
                onClick={handleReset}
              >
                {trans("Common.reset")}
              </Button>
              <Button
                className="saveButton"
                disabled={
                  filterIsLoading ||
                  mealPlanDetailsLoading ||
                  status ||
                  !validMacroPercentage ||
                  !isPercentageValid ||
                  !(
                    (formData?.recipesDetailMeal?.length ?? 0) > 0 ||
                    displayedRecipes.length > 0
                  ) ||
                  !isEachMealPercentageValid ||
                  allZero
                }
                onClick={handleSubmit}
              >
                <i className="bi bi-save mr-2 "></i>
                {trans("Common.save")}
              </Button>
            </Col>
          </Row>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col xs={12} md={6} lg={6}>
                <Form.Group controlId="formBmrTdee">
                  <FormLabel
                    label={`${trans("ClientMealPlan.bmr")}/${trans("ClientMealPlan.tdee")}`}
                  />
                  <FormControl
                    type="text"
                    placeholder={
                      trans("Common.enter") +
                      " " +
                      trans("ClientMealPlan.bmr") +
                      "/" +
                      trans("ClientMealPlan.tdee")
                    }
                    name="bmrTdee"
                    value={formData.bmrTdee === "N/A" ? "0" : formData.bmrTdee}
                    onChange={handleChange}
                    isInvalid={!!errors.bmrTdee}
                    feedback={errors.bmrTdee}
                    className="rounded-3"
                    isDisabled
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6} lg={6}>
                <Form.Group controlId="formPal">
                  <FormLabel label={trans("ClientMealPlan.pal")} />
                  <FormControl
                    type="text"
                    placeholder={
                      trans("Common.enter") + " " + trans("ClientMealPlan.pal")
                    }
                    name="pal"
                    value={
                      formData.pal === "N/A"
                        ? 0
                        : Math.round(Number(formData.pal))
                    }
                    onChange={handleChange}
                    isInvalid={!!errors.pal}
                    feedback={errors.pal}
                    className="rounded-3"
                    isDisabled
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              {/* Dietary Preferences */}
              <Col xs={12} md={6} className="mt-3">
                <FormLabel label={trans("ClientMealPlan.preferences")} />
                <Select
                  mode="multiple"
                  placeholder={
                    trans("Common.select") +
                    " " +
                    trans("ClientMealPlan.preferences")
                  }
                  value={dietaryPreferences}
                  onChange={handleDietaryPreference}
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  className="w-100"
                >
                  {clientData?.clientProvidedInputs
                    ?.find((input) => input.title === DIETARY_PREFERENCES)
                    ?.options?.map((option) => {
                      const conflicts =
                        DIETARY_CONFLICTS_BY_NAME[option.optionText] || [];

                      const isDisabled = dietaryPreferences.some(
                        (selectedId) => {
                          const selectedOption = clientData.clientProvidedInputs
                            .find(
                              (input) => input.title === DIETARY_PREFERENCES
                            )
                            ?.options.find(
                              (opt) => opt.optionId === selectedId
                            );

                          if (!selectedOption) return false;

                          const selectedConflicts =
                            DIETARY_CONFLICTS_BY_NAME[
                              selectedOption.optionText
                            ] || [];

                          return (
                            // option conflicts with selected item
                            conflicts.includes(selectedOption.optionText) ||
                            // selected item conflicts with option
                            selectedConflicts.includes(option.optionText)
                          );
                        }
                      );

                      return (
                        <Option
                          key={option.optionId}
                          value={option.optionId}
                          disabled={isDisabled}
                        >
                          {option.optionText}
                        </Option>
                      );
                    })}
                </Select>
              </Col>

              {/* Allergies */}
              <Col xs={12} md={6} className="mt-3">
                <FormLabel label={trans("ClientMealPlan.allergies")} />
                <Select
                  mode="multiple"
                  placeholder={
                    trans("Common.select") +
                    " " +
                    trans("ClientMealPlan.allergies")
                  }
                  value={allergies}
                  onChange={handleChangeAllergies}
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  className="w-100"
                >
                  {clientData?.clientProvidedInputs
                    ?.find((input) => input.title === ALLERGIES)
                    ?.options?.map((option) => (
                      <Option key={option.optionId} value={option.optionId}>
                        {option.optionText}
                      </Option>
                    ))}
                </Select>
              </Col>
            </Row>

            {/* Meal Plan Fields */}
            <Row className="align-items-center mt-4">
              <Col xs={12} md={4}>
                <h6>{formData?.mealPlanName}</h6>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <FormLabel label={trans("Common.name")} required />
                <FormControl
                  type="text"
                  placeholder={
                    trans("Common.enter") +
                    " " +
                    trans("ClientMealPlan.mealPlanName")
                  }
                  name="mealPlanName"
                  value={formData?.mealPlanName}
                  onChange={handleChange}
                  isInvalid={!!errors.mealPlanName}
                  feedback={errors.mealPlanName}
                  className="rounded-3"
                />
              </Col>
              <Col>
                <FormLabel
                  label={trans("ClientMealPlan.dailyIntake")}
                  required
                />
                <FormControl
                  type="text"
                  placeholder={
                    trans("Common.enter") +
                    " " +
                    trans("ClientMealPlan.dailyIntake")
                  }
                  name="dailyIntake"
                  value={formData.dailyIntake}
                  onChange={handleChange}
                  isInvalid={!!errors.dailyIntake}
                  feedback={errors.dailyIntake}
                  className="rounded-3"
                  suffix="Cal"
                />
                <p
                  className={
                    dailyIntakeRecommendation ? "text-info ml-2 mt-1" : ""
                  }
                >
                  {dailyIntakeRecommendation
                    ? `Recommended Calories: ${dailyIntakeRecommendation}`
                    : ""}
                </p>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <FormLabel label={trans("ClientMealPlan.meals")} required />
                <FormSelect
                  label={""}
                  name="meals"
                  value={selectedMeals}
                  onChange={handleMealsChange}
                  options={[
                    { value: "", label: trans("ClientMealPlan.selectMeal") },
                    {
                      value: "2Meals",
                      label: trans("ClientMealPlan.twoMeals"),
                    },
                    {
                      value: "3Meals",
                      label: trans("ClientMealPlan.threeMeals"),
                    },
                    {
                      value: "3Meals1Snack",
                      label: trans("ClientMealPlan.threeMealsOneSnack"),
                    },
                    {
                      value: "3Meals2Snacks",
                      label: trans("ClientMealPlan.threeMealsTwoSnack"),
                    },
                    {
                      value: "4Meals",
                      label: trans("ClientMealPlan.fourMeals"),
                    },
                    {
                      value: "4Meals1Snack",
                      label: trans("ClientMealPlan.fourMealsOneSnack"),
                    },
                  ]}
                />
                {errors.selectedMeals && (
                  <div className="text-danger small mt-1">
                    {errors.selectedMeals}
                  </div>
                )}
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <FormLabel label="Macro Split Template" />
                <div className="macro-select-wrapper">
                  <Select
                    className="w-100"
                    placeholder="Select a macro split template"
                    value={selectedMacroTemplate}
                    loading={macroSplitTemplatesLoading}
                    onChange={handleMacroTemplateChange}
                  >
                    {macroSplitTemplates?.map((template) => (
                      <Option key={template.id} value={template.id}>
                        {template.planName}
                      </Option>
                    ))}
                  </Select>
                </div>
              </Col>
            </Row>
            <Row className="mt-2">
              {/* Protein */}
              <Col>
                <FormLabel
                  label={trans("ClientMealPlan.protein")}
                  className="text-green-500"
                />
                <FormControl
                  type="text"
                  placeholder={
                    trans("Common.enter") +
                    " " +
                    trans("ClientMealPlan.protein")
                  }
                  name="proteinPercentage"
                  value={formData.proteinPercentage}
                  onChange={handleChange}
                  isInvalid={!!errors.proteinPercentage}
                  className="custom-rounded-top"
                  suffix="%"
                  isDisabled={selectedMacroTemplate !== 11}
                />
                <FormControl
                  type="text"
                  placeholder={trans("Common.enter") + " g"}
                  name="proteinGrams"
                  value={formData.proteinGrams}
                  onChange={handleChange}
                  isDisabled
                  className="custom-rounded-bottom"
                  suffix="g"
                />
              </Col>

              {/* Carbs */}
              <Col>
                <FormLabel
                  label={trans("ClientMealPlan.carbs")}
                  className="text-yellow-500"
                />
                <FormControl
                  type="text"
                  placeholder={
                    trans("Common.enter") + " " + trans("ClientMealPlan.carbs")
                  }
                  name="carbsPercentage"
                  value={formData.carbsPercentage}
                  onChange={handleChange}
                  isInvalid={!!errors.carbsPercentage}
                  className="custom-rounded-top"
                  suffix="%"
                  isDisabled={
                    selectedMacroTemplate && selectedMacroTemplate !== 11
                  }
                />
                <FormControl
                  type="text"
                  placeholder={trans("Common.enter") + " g"}
                  name="carbsGrams"
                  value={Math.round(formData.carbsGrams)}
                  onChange={handleChange}
                  isDisabled
                  className="custom-rounded-bottom"
                  suffix="g"
                />
              </Col>

              {/* Fat */}
              <Col>
                <FormLabel
                  label={trans("ClientMealPlan.fat")}
                  className="text-gray-700"
                />
                <FormControl
                  type="text"
                  placeholder={
                    trans("Common.enter") + " " + trans("ClientMealPlan.fat")
                  }
                  name="fatPercentage"
                  value={Math.round(formData.fatPercentage)}
                  onChange={handleChange}
                  isInvalid={!!errors.fatPercentage}
                  className="custom-rounded-top"
                  suffix="%"
                  isDisabled={
                    selectedMacroTemplate && selectedMacroTemplate !== 11
                  }
                />
                <FormControl
                  type="text"
                  placeholder={trans("Common.enter") + " g"}
                  name="fatGrams"
                  value={Math.round(formData.fatGrams)}
                  onChange={handleChange}
                  isDisabled
                  className="custom-rounded-bottom"
                  suffix="g"
                />
              </Col>
            </Row>
            {!isPercentageValid && (
              <p className="m-4 ms-2 text-danger">
                {trans("ClientMealPlan.proteinPercentageError")}
              </p>
            )}
            {isPercentageValid &&
              (() => {
                const protein = Number(formData?.proteinPercentage) || 0;
                const carbs = Number(formData?.carbsPercentage) || 0;
                const fat = Number(formData?.fatPercentage) || 0;

                if (protein < CONSTANTS.FIVE) {
                  return (
                    <p className="m-4 ms-2 text-danger">
                      {trans("ClientMealPlan.proteinMustBeGreaterThanFive")}
                    </p>
                  );
                } else if (carbs < CONSTANTS.FIVE) {
                  return (
                    <p className="m-4 ms-2 text-danger">
                      {trans("ClientMealPlan.carbsMustBeGreaterThanFive")}
                    </p>
                  );
                } else if (fat < CONSTANTS.FIVE) {
                  return (
                    <p className="m-4 ms-2 text-danger">
                      {trans("ClientMealPlan.fatMustBeGreaterThanFive")}
                    </p>
                  );
                } else {
                  return null;
                }
              })()}
            {selectedMeals && (
              <>
                <h6 className="mt-4">{trans("ClientMealPlan.dailyMeals")}</h6>
                <p className="text-muted">
                  {trans("ClientMealPlan.dailyMealsHeading")}
                </p>
                <div>
                  {(mealPlanMap[selectedMeals] || []).map((meal) => {
                    const percentage = mealPercentages[meal] || 0;
                    const kcal = formData?.dailyIntake
                      ? Math.round((formData?.dailyIntake * percentage) / 100)
                      : 0;

                    return (
                      <Row
                        key={meal}
                        className="align-items-center mt-2 w-75 ms-2"
                      >
                        <Col xs={4} md={4}>
                          <i className="bi bi-check-circle-fill text-success mr-2 fs-6"></i>
                          <FormLabel
                            label={`${meal?.charAt(0)?.toUpperCase()}${meal
                              ?.slice(1)
                              ?.toLowerCase()}`}
                          />
                        </Col>

                        <Col xs={4} md={4}>
                          <div className="small text-muted">
                            <strong>{kcal} Cal</strong>
                          </div>
                        </Col>

                        <Col xs={4} md={4}>
                          <FormControl
                            type="text"
                            placeholder={trans("Common.enter") + " %"}
                            name="meal"
                            value={percentage}
                            onChange={(e) =>
                              handleMealPercentageChange(meal, e.target.value)
                            }
                            onBlur={() => handleMealPercentageBlur(meal)}
                            className="rounded-2 w-100"
                            suffix="%"
                          />
                        </Col>
                      </Row>
                    );
                  })}
                </div>
                {!isEachMealPercentageValid && (
                  <p className="m-4 ms-2 text-danger">
                    {trans("ClientMealPlan.mealPercentageTotal") +
                      " " +
                      `${roundedCurrentTotal}%`}
                  </p>
                )}

                {isEachMealPercentageValid && allZero && (
                  <p className="m-4 ms-2 text-danger">
                    {trans("ClientMealPlan.mealPercentageNonZero") +
                      " " +
                      `${roundedCurrentTotal}%`}
                  </p>
                )}
              </>
            )}
            <div className="d-flex align-items-center mt-4">
              <FormLabel
                label={trans("ClientMealPlan.recipesPerMeal")}
                className="me-2 mb-0"
              />
              <FormSelect
                name="numberOfRecipesPerMeal"
                value={numberOfRecipesPerMeal}
                onChange={handleNumberOfRecipesPerMealChange}
                options={[
                  { value: 3, label: 3 },
                  { value: 4, label: 4 },
                  { value: 5, label: 5 },
                  { value: 6, label: 6 },
                ]}
                className="w-auto"
              />
            </div>
            {/* </div>  */}

            <div className="text-end">
              {mealPlanDetails && formData.recipesDetailMeal?.length > 0 && (
                <div className="d-flex align-items-end mt-3 ">
                  <Form.Check
                    type="checkbox"
                    id="resizeDailyIntakeCheckbox"
                    checked={resizeConfirmed}
                    onChange={(e) => setResizeConfirmed(e.target.checked)}
                    label={
                      <span>
                        {"Check this to generate a new plan with the updated daily calorie intake."}
                      </span>
                    }
                    className="me-2"
                  />
                </div>
              )}
              <Button
                className="saveButton mr-2"
                disabled={
                  filterIsLoading ||
                  mealPlanDetailsLoading ||
                  status ||
                  !validMacroPercentage ||
                  !isPercentageValid ||
                  !isEachMealPercentageValid ||
                  allZero
                }
                onClick={handleFetchRecipes}
              >
                {trans("ClientMealPlan.generate")}
              </Button>
            </div>

            {formData?.dailyIntake &&
              mealPercentages &&
              typeof mealPercentages === "object" &&
              Object.keys(mealPercentages).length > 0 && (
                <MealPieChart
                  mealPlanMap={mealPlanMap}
                  selectedMeals={selectedMeals}
                  mealPercentages={mealPercentages}
                  formData={formData}
                />
              )}

            <Row className="mt-4">
              <h6>Recipes</h6>
              <hr className="mt-2" />
              {mealPlanDetailsLoading || isFetchingRecipes ? (
                <Loader />
              ) : formData?.recipesDetailMeal?.length === 0 ||
                isMealsDetailsAlreadyNotFilled ? (
                displayedRecipes.length > 0 ? (
                  <>
                    {filteredRecipes?.BREAKFAST?.length > 0 && (
                      <div>
                        <h6>{trans("ClientMealPlan.breakfast")}</h6>
                        <Row className="d-flex flex-row flex-nowrap overflow-auto recipe-scroll-container gap-2 p-1 m-1">
                          {filteredRecipes?.BREAKFAST?.slice(
                            0,
                            numberOfRecipesPerMeal
                          )?.map((recipe) => (
                            <RecipesCard
                              recipe={recipe}
                              key={recipe.id}
                              mealType={MEAL_TYPES.BREAKFAST}
                              filteredRecipes={filteredRecipes?.BREAKFAST?.slice(
                                0,
                                numberOfRecipesPerMeal
                              )}
                              recipesList={filteredRecipes?.BREAKFAST}
                              handleRecipeUpdate={handleRecipeUpdate}
                              selectedMeals={MEAL_CATEGORIES.BREAKFAST}
                              formData={formData}
                              exclude={mealRestrictions}
                              mealPercentages={mealPercentages}
                              setMakeFavoriteMessage={setMakeFavoriteMessage}
                              dietaryPreferences={dietaryPreferences}
                              allergies={allergies}
                              numberOfRecipesPerMeal={numberOfRecipesPerMeal}
                            />
                          ))}
                        </Row>
                        <hr className="p-0" />
                      </div>
                    )}
                    {filteredRecipes?.LUNCH?.length > 0 && (
                      <div>
                        <h6>{trans("ClientMealPlan.lunch")}</h6>
                        <Row className="d-flex flex-row flex-nowrap overflow-auto recipe-scroll-container gap-2 p-1 m-1">
                          {filteredRecipes?.LUNCH?.slice(
                            0,
                            numberOfRecipesPerMeal
                          )?.map((recipe) => (
                            <RecipesCard
                              recipe={recipe}
                              key={recipe.id}
                              mealType={MEAL_TYPES.LUNCH}
                              filteredRecipes={filteredRecipes?.LUNCH?.slice(
                                0,
                                numberOfRecipesPerMeal
                              )}
                              recipesList={filteredRecipes?.LUNCH}
                              handleRecipeUpdate={handleRecipeUpdate}
                              selectedMeals={MEAL_CATEGORIES.LUNCH}
                              formData={formData}
                              exclude={mealRestrictions}
                              mealPercentages={mealPercentages}
                              setMakeFavoriteMessage={setMakeFavoriteMessage}
                              dietaryPreferences={dietaryPreferences}
                              allergies={allergies}
                              numberOfRecipesPerMeal={numberOfRecipesPerMeal}
                            />
                          ))}
                        </Row>
                        <hr className="p-0" />
                      </div>
                    )}

                    {filteredRecipes?.LUNCH1?.length > 0 && (
                      <div>
                        <h6>{trans("ClientMealPlan.lunch") + " 1"}</h6>
                        <Row className="d-flex flex-row flex-nowrap overflow-auto recipe-scroll-container gap-2 p-1 m-1">
                          {filteredRecipes?.LUNCH1?.slice(
                            Math.max(
                              0,
                              filteredRecipes?.LUNCH1?.length -
                                (numberOfRecipesPerMeal || 4)
                            )
                          )?.map((recipe) => (
                            <RecipesCard
                              recipe={recipe}
                              key={recipe.id}
                              mealType={MEAL_TYPES.LUNCH1}
                              filteredRecipes={filteredRecipes?.LUNCH1?.slice(
                                Math.max(
                                  0,
                                  filteredRecipes?.LUNCH1?.length -
                                    (numberOfRecipesPerMeal || 4)
                                )
                              )}
                              recipesList={filteredRecipes?.LUNCH1}
                              handleRecipeUpdate={handleRecipeUpdate}
                              selectedMeals={MEAL_CATEGORIES.LUNCH1}
                              formData={formData}
                              exclude={mealRestrictions}
                              mealPercentages={mealPercentages}
                              setMakeFavoriteMessage={setMakeFavoriteMessage}
                              dietaryPreferences={dietaryPreferences}
                              allergies={allergies}
                              numberOfRecipesPerMeal={numberOfRecipesPerMeal}
                            />
                          ))}
                        </Row>
                      </div>
                    )}

                    {filteredRecipes?.DINNER?.length > 0 && (
                      <div>
                        <h6>{trans("ClientMealPlan.dinner")}</h6>
                        <Row className="d-flex flex-row flex-nowrap overflow-auto recipe-scroll-container gap-2 p-1 m-1">
                          {filteredRecipes?.DINNER?.slice(
                            0,
                            numberOfRecipesPerMeal
                          )?.map((recipe) => (
                            <RecipesCard
                              recipe={recipe}
                              key={recipe.id}
                              mealType={MEAL_TYPES.DINNER}
                              filteredRecipes={filteredRecipes?.DINNER?.slice(
                                0,
                                numberOfRecipesPerMeal
                              )}
                              recipesList={filteredRecipes?.DINNER}
                              handleRecipeUpdate={handleRecipeUpdate}
                              selectedMeals={MEAL_CATEGORIES.DINNER}
                              formData={formData}
                              exclude={mealRestrictions}
                              mealPercentages={mealPercentages}
                              setMakeFavoriteMessage={setMakeFavoriteMessage}
                              dietaryPreferences={dietaryPreferences}
                              allergies={allergies}
                              numberOfRecipesPerMeal={numberOfRecipesPerMeal}
                            />
                          ))}
                        </Row>
                        <hr className="p-0" />
                      </div>
                    )}
                    {filteredRecipes?.SNACK?.length > 0 && (
                      <div>
                        <h6>{trans("ClientMealPlan.snack")}</h6>
                        <Row className="d-flex flex-row flex-nowrap overflow-auto recipe-scroll-container gap-2 p-1 m-1">
                          {filteredRecipes?.SNACK?.slice(
                            0,
                            numberOfRecipesPerMeal
                          )?.map((recipe) => (
                            <RecipesCard
                              recipe={recipe}
                              key={recipe.id}
                              mealType={MEAL_TYPES.SNACK}
                              filteredRecipes={filteredRecipes?.SNACK?.slice(
                                0,
                                numberOfRecipesPerMeal
                              )}
                              recipesList={filteredRecipes?.SNACK}
                              handleRecipeUpdate={handleRecipeUpdate}
                              selectedMeals={MEAL_CATEGORIES.SNACK}
                              formData={formData}
                              exclude={mealRestrictions}
                              mealPercentages={mealPercentages}
                              setMakeFavoriteMessage={setMakeFavoriteMessage}
                              dietaryPreferences={dietaryPreferences}
                              allergies={allergies}
                              numberOfRecipesPerMeal={numberOfRecipesPerMeal}
                            />
                          ))}
                        </Row>
                        <hr className="p-0" />
                      </div>
                    )}
                    {filteredRecipes?.SNACK1?.length > 0 && (
                      <div>
                        <h6>{trans("ClientMealPlan.snack") + " 1"}</h6>
                        <Row className="d-flex flex-row flex-nowrap overflow-auto recipe-scroll-container gap-2 p-1 m-1">
                          {filteredRecipes?.SNACK1?.slice(
                            Math.max(
                              0,
                              filteredRecipes?.SNACK1?.length -
                                (numberOfRecipesPerMeal || 4)
                            )
                          )?.map((recipe) => (
                            <RecipesCard
                              recipe={recipe}
                              key={recipe.id}
                              mealType={MEAL_TYPES.SNACK1}
                              filteredRecipes={filteredRecipes?.SNACK1?.slice(
                                Math.max(
                                  0,
                                  filteredRecipes?.SNACK1?.length -
                                    (numberOfRecipesPerMeal || 4)
                                )
                              )}
                              recipesList={filteredRecipes?.SNACK1}
                              handleRecipeUpdate={handleRecipeUpdate}
                              selectedMeals={MEAL_CATEGORIES.SNACK1}
                              formData={formData}
                              exclude={mealRestrictions}
                              mealPercentages={mealPercentages}
                              setMakeFavoriteMessage={setMakeFavoriteMessage}
                              dietaryPreferences={dietaryPreferences}
                              allergies={allergies}
                              numberOfRecipesPerMeal={numberOfRecipesPerMeal}
                            />
                          ))}
                        </Row>
                        <hr className="p-0" />
                      </div>
                    )}
                  </>
                ) : (
                  <p className="d-flex justify-content-center align-items-center fs-4 text-muted">
                    {trans("ClientMealPlan.recipesNotFound")}
                  </p>
                )
              ) : (
                <>
                  {groupedRecipes?.BREAKFAST?.length > 0 && (
                    <div>
                      <h6>{trans("ClientMealPlan.breakfast")}</h6>
                      <Row className="d-flex flex-row flex-nowrap overflow-auto recipe-scroll-container gap-2 p-1 m-1">
                        {groupedRecipes?.BREAKFAST?.map((recipe) => (
                          <>
                            <RecipesCard
                              recipe={recipe.recipe}
                              key={recipe.id}
                              mealType={MEAL_TYPES.BREAKFAST}
                              filteredRecipes={groupedRecipes?.BREAKFAST}
                              recipesList={filteredRecipes?.BREAKFAST}
                              handleRecipeUpdate={handleRecipeUpdate}
                              selectedMeals={MEAL_CATEGORIES.BREAKFAST}
                              formData={formData}
                              exclude={mealRestrictions}
                              mealPercentages={mealPercentages}
                              setMakeFavoriteMessage={setMakeFavoriteMessage}
                              dietaryPreferences={dietaryPreferences}
                              allergies={allergies}
                              numberOfRecipesPerMeal={numberOfRecipesPerMeal}
                            />
                          </>
                        ))}
                      </Row>
                      <hr className="p-0" />
                    </div>
                  )}
                  {groupedRecipes?.LUNCH?.length > 0 && (
                    <div>
                      <h6>{trans("ClientMealPlan.lunch")}</h6>
                      <Row className="d-flex flex-row flex-nowrap overflow-auto recipe-scroll-container gap-2 p-1 m-1">
                        {groupedRecipes?.LUNCH?.map((recipe) => (
                          <>
                            {/* {console.log("test_div_filter_recipes_111", recipe)} */}
                            <RecipesCard
                              recipe={recipe.recipe}
                              key={recipe.id}
                              mealType={MEAL_TYPES.LUNCH}
                              filteredRecipes={groupedRecipes?.LUNCH}
                              recipesList={filteredRecipes?.LUNCH}
                              handleRecipeUpdate={handleRecipeUpdate}
                              selectedMeals={MEAL_CATEGORIES.LUNCH}
                              formData={formData}
                              exclude={mealRestrictions}
                              mealPercentages={mealPercentages}
                              setMakeFavoriteMessage={setMakeFavoriteMessage}
                              dietaryPreferences={dietaryPreferences}
                              allergies={allergies}
                              numberOfRecipesPerMeal={numberOfRecipesPerMeal}
                            />
                          </>
                        ))}
                      </Row>
                      <hr className="p-0" />
                    </div>
                  )}
                  {groupedRecipes?.LUNCH1?.length > 0 && (
                    <div>
                      <h6>{trans("ClientMealPlan.lunch") + " 1"}</h6>
                      <Row className="d-flex flex-row flex-nowrap overflow-auto recipe-scroll-container gap-2 p-1 m-1">
                        {groupedRecipes?.LUNCH1?.map((recipe) => (
                          <RecipesCard
                            recipe={recipe.recipe}
                            key={recipe.id}
                            mealType={MEAL_TYPES.LUNCH1}
                            filteredRecipes={groupedRecipes?.LUNCH1}
                            recipesList={filteredRecipes?.LUNCH1}
                            handleRecipeUpdate={handleRecipeUpdate}
                            selectedMeals={MEAL_CATEGORIES.LUNCH1}
                            formData={formData}
                            exclude={mealRestrictions}
                            mealPercentages={mealPercentages}
                            setMakeFavoriteMessage={setMakeFavoriteMessage}
                            dietaryPreferences={dietaryPreferences}
                            allergies={allergies}
                            numberOfRecipesPerMeal={numberOfRecipesPerMeal}
                          />
                        ))}
                      </Row>
                      <hr className="p-0" />
                    </div>
                  )}
                  {groupedRecipes?.DINNER?.length > 0 && (
                    <div>
                      <h6>{trans("ClientMealPlan.dinner")}</h6>
                      <Row className="d-flex flex-row flex-nowrap overflow-auto recipe-scroll-container gap-2 p-1 m-1">
                        {groupedRecipes?.DINNER?.map((recipe) => (
                          <RecipesCard
                            recipe={recipe.recipe}
                            key={recipe.id}
                            mealType={MEAL_TYPES.DINNER}
                            filteredRecipes={groupedRecipes?.DINNER}
                            recipesList={filteredRecipes?.DINNER}
                            handleRecipeUpdate={handleRecipeUpdate}
                            selectedMeals={MEAL_CATEGORIES.DINNER}
                            formData={formData}
                            exclude={mealRestrictions}
                            mealPercentages={mealPercentages}
                            setMakeFavoriteMessage={setMakeFavoriteMessage}
                            dietaryPreferences={dietaryPreferences}
                            allergies={allergies}
                            numberOfRecipesPerMeal={numberOfRecipesPerMeal}
                          />
                        ))}
                      </Row>
                      <hr className="p-0" />
                    </div>
                  )}
                  {groupedRecipes?.SNACK?.length > 0 && (
                    <div>
                      <h6>{trans("ClientMealPlan.snack")}</h6>
                      <Row className="d-flex flex-row flex-nowrap overflow-auto recipe-scroll-container gap-2 p-1 m-1">
                        {groupedRecipes?.SNACK?.slice(
                          0,
                          numberOfRecipesPerMeal
                        )?.map((recipe) => (
                          <RecipesCard
                            recipe={recipe.recipe}
                            key={recipe.id}
                            mealType={MEAL_TYPES.SNACK}
                            filteredRecipes={groupedRecipes?.SNACK}
                            recipesList={filteredRecipes?.SNACK}
                            handleRecipeUpdate={handleRecipeUpdate}
                            selectedMeals={MEAL_CATEGORIES.SNACK}
                            formData={formData}
                            exclude={mealRestrictions}
                            mealPercentages={mealPercentages}
                            setMakeFavoriteMessage={setMakeFavoriteMessage}
                            dietaryPreferences={dietaryPreferences}
                            allergies={allergies}
                            numberOfRecipesPerMeal={numberOfRecipesPerMeal}
                          />
                        ))}
                      </Row>
                      <hr className="p-0" />
                    </div>
                  )}
                  {groupedRecipes?.SNACK1?.length > 0 && (
                    <div>
                      <h6>{trans("ClientMealPlan.snack") + " 1"}</h6>
                      <Row className="d-flex flex-row flex-nowrap overflow-auto recipe-scroll-container gap-2 p-1 m-1">
                        {groupedRecipes?.SNACK1?.map((recipe) => (
                          <RecipesCard
                            recipe={recipe.recipe}
                            key={recipe.id}
                            mealType={MEAL_TYPES.SNACK1}
                            filteredRecipes={groupedRecipes?.SNACK1}
                            recipesList={filteredRecipes?.SNACK1}
                            handleRecipeUpdate={handleRecipeUpdate}
                            selectedMeals={MEAL_CATEGORIES.SNACK1}
                            formData={formData}
                            exclude={mealRestrictions}
                            mealPercentages={mealPercentages}
                            setMakeFavoriteMessage={setMakeFavoriteMessage}
                            dietaryPreferences={dietaryPreferences}
                            allergies={allergies}
                            numberOfRecipesPerMeal={numberOfRecipesPerMeal}
                          />
                        ))}
                      </Row>
                      <hr className="p-0" />
                    </div>
                  )}
                </>
              )}
            </Row>
          </Form>
        </>
      )}
      {recipeIds.length > 0 && (
        <MealPlanSummary
          recipeIds={recipeIds}
          mealCount={selectedMeals}
          selectedMeals={selectedMeals}
          targetCalories={formData.dailyIntake}
          isPercentageValid={isPercentageValid}
          isEachMealPercentageValid={isEachMealPercentageValid}
          targetProtein={formData.proteinGrams}
          targetCarbs={formData.carbsGrams}
          targetFat={formData.fatGrams}
        />
      )}
      {toastConfig && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </div>
  );
};

export default ClientMealsDetails;
